/**
 * Language Picker Modal
 * Searchable list of languages.
 * Follows the CountryPickerModal pattern.
 */

import { useState, useMemo } from 'react';
import { CloseButton } from '../ui/CloseButton';
import { SearchInput } from '../ui/SearchInput';
import { NRLogo } from '../ui/NRLogo';
import { LANGUAGES, type LanguageCode } from '../../i18n/languages';
import { useT } from '../../i18n';

export interface LanguagePickerModalProps {
  onClose: () => void;
  onSelect: (code: LanguageCode) => void;
  /** Currently selected language code */
  selectedCode?: string;
}

export function LanguagePickerModal({
  onClose,
  onSelect,
  selectedCode,
}: LanguagePickerModalProps) {
  const t = useT();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const raw = search.trim().toLowerCase();

    // If no search, put selected item first
    if (!raw) {
      if (selectedCode) {
        const selected = LANGUAGES.find((l) => l.code === selectedCode);
        if (selected) {
          const rest = LANGUAGES.filter((l) => l.code !== selectedCode);
          return [selected, ...rest];
        }
      }
      return LANGUAGES;
    }

    return LANGUAGES.filter(
      (l) =>
        l.name.toLowerCase().includes(raw) ||
        l.english.toLowerCase().includes(raw) ||
        l.code.includes(raw)
    );
  }, [search, selectedCode]);

  return (
    <>
      {/* Modal header — 128px with blurred bg */}
      <div className="relative shrink-0" style={{ height: 128, background: 'var(--t-header-bg)', backdropFilter: 'blur(var(--t-header-blur))', WebkitBackdropFilter: 'blur(var(--t-header-blur))' }}>
        <div className="absolute" style={{ left: 12, top: 20 }}>
          <NRLogo width={46} />
        </div>
        <p className="absolute text-[16px] text-center truncate" style={{ top: 28, left: 64, right: 64, color: 'var(--t-text)', lineHeight: '24px' }}>
          {t('modal.selectLanguage')}
        </p>
        <div className="absolute" style={{ right: 12, top: 20 }}>
          <CloseButton onClick={onClose} />
        </div>
        <div className="absolute" style={{ bottom: 8, left: 12, right: 12 }}>
          <SearchInput value={search} onChange={setSearch} inline />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scroll px-3">
        {filtered.map((lang, i) => {
          const isSelected = selectedCode === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => onSelect(lang.code)}
              className="flex items-center w-full card-press hover-item modal-item-in"
              style={{ gap: 16, animationDelay: `${Math.min(i * 45, 350)}ms` }}
            >
              <div className="flex-1 flex items-center" style={{ borderBottom: '1px solid var(--t-card-border)', paddingTop: 20, paddingBottom: 20, paddingLeft: 4, gap: 10 }}>
                <span className="flex-1 text-[18px] text-left" style={{ color: 'var(--t-text)', lineHeight: '20px' }}>
                  {lang.name}
                </span>
                <span className="text-[13px]" style={{ color: 'var(--t-text-muted)' }}>
                  {lang.english}
                </span>
                {isSelected && (
                  <div style={{ width: 40, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="var(--t-success, #4CD964)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-10 text-[14px]" style={{ color: 'var(--t-text-muted)' }}>
            {t('modal.noLanguages')}
          </div>
        )}
      </div>
    </>
  );
}
